import { Box, Flex } from "@chakra-ui/layout";
import {
  Checkbox,
  Radio,
  RadioGroup,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { Col, Row, Tooltip } from "antd";
import { PeriodoArrecadacaoEnum } from "features/arrecadacoes/enums";
import moment from "moment";
import Field from "components/Input/Field";
import SelectContaBancaria from "components/Input/SelectContaBancaria";
import SelectContaCategoria from "components/Input/SelectContaCategoria";
import SelectMonthYear from "components/Input/SelectMonthYear";
import { FormArrecadacaoChildrenFormProps } from "./FormArrecadacao";
import { TitleWrapper } from "./TitleWrapper";
import { OperacaoEnum } from "core/resources/enums/conta";
import SelectGrupoDesconto from "components/Input/SelectGrupoDesconto";
import SelectCentroDeCusto from "components/Input/SelectCentroDeCusto";
import { FaInfoCircle } from "react-icons/fa";
import InputMoney from "components/Input/InputMoney";
import { useRef } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";

interface FormGeralProps {
  month: string;
  year: string;
  idArrecadacao?: string;
}

function FormGeral({
  idArrecadacao,
  control,
  watch,
  setValue,
  current,
  year,
  month,
}: FormArrecadacaoChildrenFormProps & FormGeralProps) {
  const isUpdate = !!idArrecadacao;
  const centroDeCustoId = watch("centroDeCustoId");

  const centroDeCustoAnterior = useRef(centroDeCustoId);

  const mudouCentroDeCusto = centroDeCustoId !== centroDeCustoAnterior.current;

  const isDataPersonalizada =
    watch("periodoArrecadacaoId") === PeriodoArrecadacaoEnum.VariosMeses;

  const processarAutomaticamente = watch("gerarAutomatico") === true;
  const diaVencimento = watch("diaVencimento");
  const diaProcessarAutomatico = watch("diaGerarAutomatico");

  const diaVencimentoAnteriorAProcessamento =
    diaVencimento &&
    diaProcessarAutomatico &&
    diaVencimento < diaProcessarAutomatico;

  const complemento = watch("complemento");

  const dataInicio = watch("dataInicio");

  return (
    <Flex
      display={current ? undefined : "none"}
      flexDir="column"
      flex={1}
      w="100%"
    >
      <Box as={Row} mb={6}>
        <Col sm={24}>
          <Field
            control={control}
            name="extra"
            render={({ field }) => (
              <Checkbox
                isChecked={!!field.value}
                onChange={() => field.onChange(!field.value)}
              >
                Boleto extra
              </Checkbox>
            )}
          />
        </Col>
      </Box>

      <Box as={Row} w={"100%"} mb={10}>
        <Col sm={10}>
          <Field
            shouldUnregister
            label="Conta bancária"
            control={control}
            name="tenantContaId"
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectContaBancaria
                isInvalid={isInvalid}
                value={field.value}
                onChange={field.onChange}
                markerDefault
                incluirInativas={false}
              />
            )}
          />
        </Col>
      </Box>

      <Box as={Row} mb={8}>
        <Col sm={24}>
          <TitleWrapper>Quando será arrecadado?</TitleWrapper>
          <Field
            control={control}
            name="periodoArrecadacaoId"
            rules={{
              required: current,
            }}
            render={({ field, isInvalid }) => (
              <RadioGroup
                w="fit-content"
                display="flex"
                flexDir="column"
                value={field.value}
                onChange={field.onChange}
              >
                <Radio
                  isInvalid={isInvalid}
                  value={PeriodoArrecadacaoEnum.Sempre}
                  mb={4}
                >
                  Sempre (recomendado)
                </Radio>
                <Radio
                  isInvalid={isInvalid}
                  value={PeriodoArrecadacaoEnum.Unico}
                  mb={4}
                >
                  Apenas{" "}
                  {moment(`${year}-${month}`, "YYYY-MM").format("MMM/YYYY")}
                </Radio>
                <Radio
                  isInvalid={isInvalid}
                  value={PeriodoArrecadacaoEnum.VariosMeses}
                >
                  Data personalizada
                </Radio>
              </RadioGroup>
            )}
          />
        </Col>
      </Box>

      {isDataPersonalizada ? (
        <Box as={Row} mb={4} gutter={16}>
          <Col sm={10}>
            <Field
              shouldUnregister
              control={control}
              name="dataInicio"
              label="Mês inicial"
              rules={{
                required: current,
              }}
              render={({ field }) => (
                <SelectMonthYear
                  value={field.value ? moment(field.value) : undefined}
                  onChange={(val) =>
                    field.onChange(val?.startOf("month").format("YYYY-MM-DD"))
                  }
                  getPopupContainer={(triggerNode: any) =>
                    triggerNode.parentNode
                  }
                  style={{ width: "100%" }}
                />
              )}
            />
          </Col>

          <Col sm={10}>
            <Field
              shouldUnregister
              control={control}
              name="dataFim"
              label="Mês final"
              rules={{
                required: current,
              }}
              render={({ field }) => (
                <SelectMonthYear
                  value={field.value ? moment(field.value) : undefined}
                  onChange={(val) =>
                    field.onChange(val?.endOf("month").format("YYYY-MM-DD"))
                  }
                  disabledDate={(date) =>
                    dataInicio
                      ? date.isSameOrBefore(moment(dataInicio), "month")
                      : false
                  }
                  getPopupContainer={(triggerNode: any) => {
                    return triggerNode.parentNode;
                  }}
                  style={{ width: "100%" }}
                />
              )}
            />
          </Col>
        </Box>
      ) : null}

      <Box as={Row} mb={4}>
        <Col sm={6}>
          <Field
            rules={{ required: true }}
            control={control}
            name="diaVencimento"
            label="Dia de Vencimento"
            render={({ field }) => (
              <InputMoney
                value={field.value}
                onChange={field.onChange}
                placeholder="0"
                maxValue={31}
                minValue={1}
                decimalScale={0}
                decimalsLimit={0}
                step={1}
              />
            )}
          />
        </Col>
      </Box>

      <Box as={Row} mb={4} gutter={[16, 16]} display="flex" alignItems="center">
        <Col sm={24}>
          <Field
            control={control}
            name="gerarAutomatico"
            render={({ field, isInvalid }) => (
              <Checkbox
                isChecked={field.value}
                isInvalid={isInvalid}
                onChange={() => {
                  field.onChange(!field.value);
                }}
              >
                Processar automaticamente
              </Checkbox>
            )}
          />
        </Col>

        {processarAutomaticamente ? (
          <Col sm={12}>
            <Field
              rules={{ required: true }}
              control={control}
              shouldUnregister
              name="diaGerarAutomatico"
              label="Dia para processar automaticamente"
              render={({ field }) => (
                <InputMoney
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="0"
                  maxValue={31}
                  minValue={1}
                  decimalScale={0}
                  decimalsLimit={0}
                  step={1}
                />
              )}
            />
          </Col>
        ) : null}

        <Col sm={24}>
          {processarAutomaticamente && diaVencimentoAnteriorAProcessamento ? (
            <Alert status="warning" mb={2} fontSize={12}>
              <AlertIcon />A arrecadação será processada no dia{" "}
              {diaProcessarAutomatico} do mês anterior ao vencimento.
            </Alert>
          ) : null}

          {processarAutomaticamente && !diaVencimentoAnteriorAProcessamento ? (
            <Alert status="info" mb={2} fontSize={12}>
              <AlertIcon />A arrecadação será processada no dia{" "}
              {diaProcessarAutomatico} do mesmo mês do vencimento.
            </Alert>
          ) : null}
        </Col>
      </Box>

      <TitleWrapper>
        Conta categoria, grupos de desconto e centro de custo
      </TitleWrapper>
      <Box as={Row} mb={4} gutter={16}>
        <Col sm={12}>
          <Field
            label="Categoria"
            control={control}
            rules={{ required: true }}
            name="categoriaFinanceiraId"
            render={({ field, isInvalid }) => (
              <SelectContaCategoria
                isInvalid={isInvalid}
                value={field.value}
                onChange={(v, item) => {
                  field.onChange(v);
                  if (item) {
                    setValue("centroDeCustoId", item.centroDeCustoId);
                  }
                }}
                operacao={OperacaoEnum.Receita}
                disableNotClassifiableNodes
              />
            )}
          />
        </Col>

        <Col sm={12}>
          <Field
            label="Grupos de desconto"
            control={control}
            name="gruposDescontoIds"
            render={({ field, isInvalid }) => (
              <SelectGrupoDesconto
                isInvalid={isInvalid}
                {...field}
                mode="multiple"
                allowClear
              />
            )}
          />
        </Col>
      </Box>

      <Box as={Row} mb={4} gutter={16}>
        <Col sm={12}>
          <Field
            shouldUnregister
            control={control}
            name="centroDeCustoId"
            label={"Centro de custo"}
            rules={{
              required: false,
            }}
            render={({ field, isInvalid }) => (
              <SelectCentroDeCusto
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          {isUpdate && mudouCentroDeCusto ? (
            <>
              <Field
                shouldUnregister
                control={control}
                name="mudarCentroDeCustoRetroativo"
                rules={{
                  required: false,
                }}
                render={({ field, isInvalid }) => (
                  <Flex alignItems="center" gap={2} mt={2}>
                    <Checkbox
                      isChecked={field.value}
                      isInvalid={isInvalid}
                      onChange={() => {
                        field.onChange(!field.value);
                      }}
                    >
                      Modificar retroativamente
                    </Checkbox>
                    <Box as="span" ml={1}>
                      <Tooltip title="Isso irá modificar retroativamente os centros de custos de contas já lançadas dessa arrecadação. Para contas já liquidadas, ocorrerá a transferência de saldo entre os centros de custo.">
                        <AiOutlineQuestionCircle cursor={"pointer"} />
                      </Tooltip>
                    </Box>
                  </Flex>
                )}
              />
            </>
          ) : null}
        </Col>
      </Box>

      <Box as={Row} mb={4}>
        <Col sm={24}>
          <Field control={control} name="complemento" label="Complemento" />
        </Col>
      </Box>

      {isDataPersonalizada ? (
        <Box as={Row} mb={4} gutter={16}>
          <Col sm={24}>
            <Field
              shouldUnregister
              control={control}
              name="complementoAutomaticoParcelas"
              render={({ field, isInvalid }) => (
                <Flex gap={3}>
                  <Checkbox
                    isChecked={field.value}
                    isInvalid={isInvalid}
                    onChange={() => {
                      field.onChange(!field.value);
                    }}
                  >
                    Complemento automático de parcelas
                  </Checkbox>

                  <Tooltip
                    title={
                      <>
                        Com isso ativo, o complemento da composição referente a
                        essa arrecadação incluirá automaticamente o número da
                        parcela de acordo com o mês lançado.
                        <br />
                        <b>
                          Exemplo: Parcela 1/10
                          {complemento ? " - " + complemento : ""}
                        </b>
                      </>
                    }
                    overlayInnerStyle={{
                      width: "500px",
                    }}
                  >
                    <FaInfoCircle
                      fontSize={16}
                      style={{
                        display: "inline-block",
                        marginTop: 2,
                      }}
                    />
                  </Tooltip>
                </Flex>
              )}
            />
          </Col>
        </Box>
      ) : null}
    </Flex>
  );
}

export default FormGeral;
